import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import useStaffHeaders from '../../hooks/useStaffHeaders';
import { fetchCourseAccess } from '../../services/learnService';
import './CreativeSamuraiLanding.css';

const HERO_BG = process.env.PUBLIC_URL + '/courses/creative-samurai/samurai-hero.jpg';
const LOGO = process.env.PUBLIC_URL + '/arvix-logo-white.png';
const INSTRUCTOR_IMG = process.env.PUBLIC_URL + '/courses/creative-samurai/instructor.png';

const PROJECTS = [
  { num: '01', tag: 'Week 2 · Illustrator', name: 'Icon Set Design', desc: 'Design 5 professional icons using vector tools, pathfinder, and advanced pen techniques.' },
  { num: '02', tag: 'Week 3 · Branding', name: 'Professional Logo', desc: 'Research, sketch, and execute a complete logo from concept to final delivery file.' },
  { num: '03', tag: 'Week 4 · Identity', name: 'Brand Identity Package', desc: 'Typography, business cards and stationery, your first mini brand identity system.' },
  { num: '04', tag: 'Week 5 · Photoshop', name: 'Social Media Post', desc: 'Design a polished social post using layers, smart objects and proper export settings.' },
  { num: '05', tag: 'Week 6 · Photoshop', name: 'Promotional Poster', desc: 'A cinematic promotional poster using masks, blend modes and advanced color grading.' },
  { num: '06', tag: 'Final · Full Stack', name: 'Complete Brand Identity', desc: 'Logo, business card, letterhead, social post, poster and brand mockup, your graduation project.' },
];

const OUTCOMES = [
  'Create professional logos and brand identities that win clients',
  'Design scroll-stopping social media and marketing materials',
  'Use Adobe Illustrator and Photoshop with full confidence',
  'Build a strong, industry-ready portfolio on Behance',
  'Start freelancing and attract clients on Fiverr & Upwork',
  'Present design projects like an industry-level designer',
];

const STATS = [
  { num: '8', label: 'Weeks' },
  { num: '32', label: 'Classes' },
  { num: '2', label: 'Softwares' },
  { num: '6+', label: 'Projects' },
  { num: '৳4,400', label: 'Course Fee' },
];

const INSTRUCTOR_STATS = [
  { num: '5+', label: 'Years Experience' },
  { num: '200+', label: 'Projects Delivered' },
  { num: '3+', label: 'Industries Served' },
];

const INSTRUCTOR_TAGS = [
  'Brand Identity', 'Logo Design', 'Social Media Design',
  'Digital Visuals', 'Adobe Illustrator', 'Adobe Photoshop',
];

const CreativeSamuraiLanding = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [enrollmentCount, setEnrollmentCount] = useState(null);
  const [countLoading, setCountLoading] = useState(true);

  const { user, isAuthenticated } = useAuth();
  const { getHeaders, hasToken } = useStaffHeaders();
  // null = unknown / visitor, otherwise { enrolled, can_watch, role }
  const [access, setAccess] = useState(null);

  useEffect(() => {
    if (!hasToken) {
      setAccess(null);
      return undefined;
    }
    let cancelled = false;
    fetchCourseAccess('creative-samurai', getHeaders())
      .then((a) => !cancelled && setAccess(a))
      .catch(() => !cancelled && setAccess(null));
    return () => {
      cancelled = true;
    };
  }, [hasToken, getHeaders]);

  const canWatch = Boolean(access?.can_watch);
  const LEARN_PATH = '/learn/creative-samurai';

  const hamburgerRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Enrollment count from API
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/creative-enrollments/count`);
        if (!res.ok) return;
        const data = await res.json();
        const total = data?.data?.total ?? 0;
        if (total > 0) setEnrollmentCount(total);
      } catch (_) {} finally {
        setCountLoading(false);
      }
    };
    load();
  }, []);

  // Scroll reveal via IntersectionObserver
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08 }
    );
    document.querySelectorAll('.cs-reveal').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Close mobile menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        hamburgerRef.current && !hamburgerRef.current.contains(e.target) &&
        mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)
      ) {
        setMobileMenuOpen(false);
        document.body.style.overflow = '';
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Match original HTML: dark body background + no scroll-padding-top
  useEffect(() => {
    const prevBodyBg = document.body.style.backgroundColor;
    const prevBodyColor = document.body.style.color;
    const prevScrollPadding = document.documentElement.style.scrollPaddingTop;

    document.body.style.backgroundColor = '#080808';
    document.body.style.color = '#F2F2F2';
    document.documentElement.style.scrollPaddingTop = '0';

    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.body.style.color = prevBodyColor;
      document.documentElement.style.scrollPaddingTop = prevScrollPadding;
    };
  }, []);

  // Restore body overflow on unmount
  useEffect(() => {
    return () => { document.body.style.overflow = ''; };
  }, []);

  const toggleMobileMenu = () => {
    const next = !mobileMenuOpen;
    setMobileMenuOpen(next);
    document.body.style.overflow = next ? 'hidden' : '';
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    document.body.style.overflow = '';
  };

  return (
    <div className="cs-page">
      <Helmet>
        <title>Creative Samurai Volume 1 | Graphic Design Course | Arvix Creative</title>
        <meta name="description" content="From zero to freelance-ready in 8 weeks. Master Adobe Illustrator & Photoshop, build a killer portfolio, and start earning. 32 hands-on classes with Arvix Creative." />
        <meta name="keywords" content="graphic design course, adobe illustrator course, adobe photoshop course, creative samurai, arvix creative course, freelancing graphic design, learn graphic design Bangladesh" />
        <meta property="og:title" content="Creative Samurai Volume 1 | Graphic Design Course | Arvix Creative" />
        <meta property="og:description" content="From zero to freelance-ready in 8 weeks. Master Adobe Illustrator & Photoshop with 32 hands-on classes, real projects, and expert instruction." />
        <meta property="og:image" content="https://arvixcreative.com/courses/creative-samurai/samurai-hero.jpg" />
        <meta name="theme-color" content="#E8000D" />
      </Helmet>

      {/* ── NAV ─────────────────────────────────────────── */}
      <nav className="cs-nav">
        <a href="/" className="cs-nav-logo" aria-label="Arvix Creative home">
          <img src={LOGO} alt="Arvix Creative" />
        </a>
        <ul className="cs-nav-links">
          <li><a href="#curriculum">Curriculum</a></li>
          <li><a href="#projects">Projects</a></li>
          <li><a href="#outcomes">Outcomes</a></li>
          <li><a href="#instructor">Instructor</a></li>
          {canWatch ? (
            <li><Link to={LEARN_PATH} className="cs-nav-cta cs-nav-cta-watch">▶ Watch Classes</Link></li>
          ) : (
            <>
              {!isAuthenticated && (
                <li><Link to="/login" state={{ from: LEARN_PATH }} className="cs-nav-login">Student Login</Link></li>
              )}
              <li><a href="#enroll" className="cs-nav-cta">Next Batch</a></li>
            </>
          )}
        </ul>
        <button
          ref={hamburgerRef}
          className={`cs-nav-hamburger${mobileMenuOpen ? ' open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Menu"
        >
          <span /><span /><span />
        </button>
      </nav>

      {/* ── MOBILE MENU ─────────────────────────────────── */}
      <div ref={mobileMenuRef} className={`cs-nav-mobile-menu${mobileMenuOpen ? ' open' : ''}`}>
        <a href="#curriculum" onClick={closeMobileMenu}>Curriculum</a>
        <a href="#projects" onClick={closeMobileMenu}>Projects</a>
        <a href="#outcomes" onClick={closeMobileMenu}>Outcomes</a>
        <a href="#instructor" onClick={closeMobileMenu}>Instructor</a>
        {canWatch ? (
          <Link to={LEARN_PATH} className="mob-cta" onClick={closeMobileMenu}>▶ Watch Classes</Link>
        ) : (
          <>
            {!isAuthenticated && (
              <Link to="/login" state={{ from: LEARN_PATH }} onClick={closeMobileMenu}>Student Login</Link>
            )}
            <a href="#enroll" className="mob-cta" onClick={closeMobileMenu}>Next Enrollment in Nov 2026</a>
          </>
        )}
      </div>

      {/* ── CONTACT BAR ─────────────────────────────────── */}
      <div className="cs-contact-bar">
        <a className="cs-contact-item" href="mailto:arvixcreative@gmail.com">
          <div className="cs-contact-icon mail">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E8000D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </div>
          <div className="cs-contact-text">
            <span className="cs-contact-label">Email Us</span>
            <span className="cs-contact-value">arvixcreative@gmail.com</span>
          </div>
        </a>
        <div className="cs-contact-divider" />
        <a className="cs-contact-item" href="https://wa.me/8801645076455" target="_blank" rel="noopener noreferrer">
          <div className="cs-contact-icon wa">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
            </svg>
          </div>
          <div className="cs-contact-text">
            <span className="cs-contact-label">WhatsApp</span>
            <span className="cs-contact-value">01645-076455</span>
          </div>
        </a>
      </div>

      {/* ── HERO ────────────────────────────────────────── */}
      <div className="cs-hero">
        <div className="cs-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="cs-hero-overlay" />
        <div className="cs-hero-red-glow" />
        <div className="cs-hero-noise" />
        <div className="cs-hero-bg-word">SAMURAI</div>
        <div className="cs-hero-content">
          <div className="cs-eyebrow">Arvix Creative · Professional Course · 2026</div>
          <h1>
            Creative<br />Samurai
            <span className="cs-vol">Volume 1</span>
          </h1>
          <div className="cs-hero-tagline">Graphic Design · Beginner to Professional</div>
          <p className="cs-hero-desc">
            From zero to freelance-ready in 8 weeks. Master Adobe Illustrator &amp; Photoshop,
            build a killer portfolio, and start earning. 32 hands-on classes, real projects, real results.
          </p>
          <div className="cs-price-block">
            <div className="cs-price-row">
              <div className="cs-price-amount">৳ 4,400</div>
              <div className="cs-price-original">৳ 5,500</div>
              <div className="cs-price-discount-badge">20% OFF</div>
            </div>
            <div className="cs-price-meta">
              <div className="cs-price-tag">Course Fee</div>
              <div className="cs-price-full">Limited Time</div>
            </div>
          </div>
          <div className="cs-hero-pills">
            <span className="cs-pill">2 Months</span>
            <span className="cs-pill">32 Classes</span>
            <span className="cs-pill">4 Days / Week</span>
            <span className="cs-pill">Beginner to Pro</span>
          </div>
          {canWatch && (
            <div className="cs-student-strip" role="status">
              <span className="cs-student-dot" />
              <span>
                {access.enrolled ? `Welcome back${user?.name ? `, ${user.name.split(' ')[0]}` : ''}. ` : 'Teacher / admin preview. '}
                Your recorded classes are ready.
              </span>
            </div>
          )}
          <div className="cs-hero-btns">
            {canWatch ? (
              <>
                <Link to={LEARN_PATH} className="cs-btn-primary">▶ Watch Classes</Link>
                <a href="#curriculum" className="cs-btn-ghost">View Curriculum</a>
              </>
            ) : (
              <>
                <a href="#enroll" className="cs-btn-primary">Next Enrollment in Nov 2026</a>
                <a href="#curriculum" className="cs-btn-ghost">View Curriculum</a>
              </>
            )}
          </div>
          {isAuthenticated && access && !canWatch && (
            <p className="cs-student-note">
              Signed in as {user?.email}. Recorded classes unlock once your Creative Samurai enrollment is confirmed.
            </p>
          )}
          {!isAuthenticated && (
            <p className="cs-student-note">
              Already enrolled? <Link to="/login" state={{ from: LEARN_PATH }}>Sign in</Link> or{' '}
              <Link to="/activate" state={{ from: LEARN_PATH }}>activate your student account</Link> to watch the recorded classes.
            </p>
          )}
        </div>
      </div>

      {/* ── STATS BAR ───────────────────────────────────── */}
      <div className="cs-stats-bar">
        {STATS.map(({ num, label }) => (
          <div key={label} className="cs-stat cs-reveal">
            <div className="cs-stat-num">{num}</div>
            <div className="cs-stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* ── SOFTWARE ────────────────────────────────────── */}
      <section className="cs-software-section" id="software">
        <div className="cs-section-eyebrow">Tools You'll Master</div>
        <div className="cs-section-title">Industry-Standard<br />Software</div>
        <div className="cs-software-grid">
          <div className="cs-software-card cs-reveal">
            <div className="cs-sw-icon">Ai</div>
            <div className="cs-sw-name">Adobe Illustrator</div>
            <p className="cs-sw-desc">
              Vector graphics, logo design, icon creation, typography, branding, and business stationery, the core toolkit of every professional designer.
            </p>
          </div>
          <div className="cs-software-card cs-reveal">
            <div className="cs-sw-icon">Ps</div>
            <div className="cs-sw-name">Adobe Photoshop</div>
            <p className="cs-sw-desc">
              Photo editing, retouching, poster design, social media visuals, layer masks, blend modes,
              and background removal, where ideas become images.
            </p>
          </div>
        </div>
      </section>

      {/* ── CURRICULUM ──────────────────────────────────── */}
      <section className="cs-curriculum-section" id="curriculum">
        <div className="cs-section-eyebrow">Course Structure</div>
        <div className="cs-section-title">8 Weeks.<br />Two Months.<br />One Career.</div>
        <div className="cs-month-grid">
          <div className="cs-month-card cs-reveal">
            <div className="cs-month-label">Month 1 · Weeks 1 to 4</div>
            <div className="cs-month-title">Adobe Illustrator &amp; Logo Design</div>
            <div className="cs-weeks">
              <div className="cs-week">
                <div className="cs-week-title">Week 1: Design Fundamentals</div>
                <div className="cs-week-item">Introduction to graphic design &amp; career opportunities</div>
                <div className="cs-week-item">Design principles, color theory &amp; typography</div>
                <div className="cs-week-item">Branding basics &amp; Illustrator interface</div>
                <div className="cs-week-item">Shape tools, selection tools &amp; basic object creation</div>
              </div>
              <div className="cs-week">
                <div className="cs-week-title">Week 2: Vector Design</div>
                <div className="cs-week-item">Pen tool fundamentals</div>
                <div className="cs-week-item">Pathfinder &amp; Shape Builder tools</div>
                <div className="cs-week-item">Strokes, fills &amp; gradients</div>
                <div className="cs-week-item">Vector tracing &amp; icon design · <em style={{ color: '#E8000D' }}>Project: 5-Icon Set</em></div>
              </div>
              <div className="cs-week">
                <div className="cs-week-title">Week 3: Logo Design Mastery</div>
                <div className="cs-week-item">Logo design principles</div>
                <div className="cs-week-item">Logo research &amp; sketching</div>
                <div className="cs-week-item">Building logos in Illustrator</div>
                <div className="cs-week-item">Logo presentation techniques · <em style={{ color: '#E8000D' }}>Project: Pro Logo</em></div>
              </div>
              <div className="cs-week">
                <div className="cs-week-title">Week 4: Branding &amp; Identity</div>
                <div className="cs-week-item">Typography in branding</div>
                <div className="cs-week-item">Custom lettermark logo design</div>
                <div className="cs-week-item">Brand identity design</div>
                <div className="cs-week-item">Business card &amp; stationery · <em style={{ color: '#E8000D' }}>Project: Mini Brand Package</em></div>
              </div>
            </div>
          </div>
          <div className="cs-month-card cs-reveal">
            <div className="cs-month-label">Month 2 · Weeks 5 to 8</div>
            <div className="cs-month-title">Adobe Photoshop &amp; Pro Design</div>
            <div className="cs-weeks">
              <div className="cs-week">
                <div className="cs-week-title">Week 5: Photoshop Fundamentals</div>
                <div className="cs-week-item">Photoshop interface &amp; layer management</div>
                <div className="cs-week-item">Selection &amp; transform tools</div>
                <div className="cs-week-item">Image retouching basics</div>
                <div className="cs-week-item">Smart objects &amp; export settings · <em style={{ color: '#E8000D' }}>Project: Social Media Post</em></div>
              </div>
              <div className="cs-week">
                <div className="cs-week-title">Week 6: Advanced Photoshop</div>
                <div className="cs-week-item">Layer masks &amp; clipping masks</div>
                <div className="cs-week-item">Adjustment layers &amp; color correction</div>
                <div className="cs-week-item">Blend modes &amp; effects</div>
                <div className="cs-week-item">Background removal · <em style={{ color: '#E8000D' }}>Project: Promotional Poster</em></div>
              </div>
              <div className="cs-week">
                <div className="cs-week-title">Week 7: Social Media &amp; Advertising</div>
                <div className="cs-week-item">Social media post design</div>
                <div className="cs-week-item">Banner &amp; product advertisement design</div>
                <div className="cs-week-item">Thumbnail &amp; cover design</div>
                <div className="cs-week-item">Social media campaign project</div>
                <div className="cs-week-item"><em style={{ color: '#E8000D' }}>Project: Full Social Media Campaign</em></div>
              </div>
              <div className="cs-week">
                <div className="cs-week-title">Week 8: Portfolio &amp; Freelancing</div>
                <div className="cs-week-item">Portfolio development &amp; Behance setup</div>
                <div className="cs-week-item">Freelancing on Fiverr &amp; Upwork</div>
                <div className="cs-week-item">Client communication &amp; pricing strategy</div>
                <div className="cs-week-item">Final portfolio review &amp; career roadmap</div>
                <div className="cs-week-item"><em style={{ color: '#E8000D' }}>Final Project: Complete Brand Identity</em></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROJECTS ────────────────────────────────────── */}
      <section className="cs-projects-section" id="projects">
        <div className="cs-section-eyebrow">What You'll Build</div>
        <div className="cs-section-title">Real Projects.<br />Real Portfolio.</div>
        <div className="cs-projects-grid">
          {PROJECTS.map(({ num, tag, name, desc }) => (
            <div key={num} className="cs-project-card cs-reveal">
              <div className="cs-project-num">{num}</div>
              <div className="cs-project-tag">{tag}</div>
              <div className="cs-project-name">{name}</div>
              <p className="cs-project-desc">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── OUTCOMES ────────────────────────────────────── */}
      <section className="cs-outcomes-section" id="outcomes">
        <div className="cs-section-eyebrow">After This Course</div>
        <div className="cs-section-title">What You'll<br />Walk Away With</div>
        <div className="cs-outcomes-inner">
          <div className="cs-outcomes-list">
            {OUTCOMES.map((text, i) => (
              <div key={i} className="cs-outcome-item cs-reveal">
                <div className="cs-outcome-check" />
                <div className="cs-outcome-text">{text}</div>
              </div>
            ))}
          </div>
          <div>
            <div className="cs-outcomes-bonus cs-reveal">
              <div className="cs-bonus-title">Bonus Classes</div>
              <div className="cs-bonus-item">Canva for Designers: fast-track client work</div>
              <div className="cs-bonus-item">Adobe Firefly &amp; AI Tools: stay ahead of the curve</div>
              <div className="cs-bonus-item">Design Presentation Skills: pitch and win projects</div>
              <div className="cs-bonus-item">Real Client Project Workflow: handle actual briefs</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INSTRUCTOR ──────────────────────────────────── */}
      <section className="cs-instructor-section" id="instructor">
        <div className="cs-section-eyebrow">Meet Your Instructor</div>
        <div className="cs-section-title">Your Instructor</div>
        <div className="cs-instructor-card cs-reveal">
          <div className="cs-instructor-img-wrap">
            <img src={INSTRUCTOR_IMG} alt="Tanvir Islam, Creative Visualizer" />
            <div className="cs-instructor-img-glow" />
          </div>
          <div className="cs-instructor-info">
            <div className="cs-instructor-badge">Creative Visualizer · Arvix Creative</div>
            <h2 className="cs-instructor-name">Tanvir Islam</h2>
            <div className="cs-instructor-title">Graphic Designer &amp; Creative Visualizer</div>
            <a href="mailto:arvixcreative@gmail.com" className="cs-instructor-email">
              <span>✉</span> arvixcreative@gmail.com
            </a>
            <p className="cs-instructor-bio">
              Tanvir Islam is a professional graphic designer and Creative Visualizer, and the founder of Arvix Creative.
              With 5 years of hands-on experience, he has built brand identities, social media design systems, and digital
              visuals for clients across industries. He currently works as a Graphics Designer at{' '}
              <strong style={{ color: '#F2F2F2' }}>July Mass Uprising Memorial Museum</strong>. His mission:{' '}
              <em style={{ color: '#E8000D', fontStyle: 'normal', fontWeight: 600 }}>Visuals that go beyond imagination.</em>
            </p>
            <div className="cs-instructor-stats">
              {INSTRUCTOR_STATS.map(({ num, label }) => (
                <div key={label} className="cs-i-stat">
                  <div className="cs-i-stat-num">{num}</div>
                  <div className="cs-i-stat-label">{label}</div>
                </div>
              ))}
            </div>
            <div className="cs-instructor-tags">
              {INSTRUCTOR_TAGS.map((tag) => (
                <span key={tag} className="cs-i-tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ENROLL ──────────────────────────────────────── */}
      <section className="cs-enroll-section" id="enroll">
        <div className="cs-enroll-bg-text">CLOSED</div>
        <div className="cs-section-eyebrow">Enrollment Closed</div>
        <div className="cs-section-title">Class Has Started</div>
        <p className="cs-enroll-desc">
          Creative Samurai Volume 1 began on Aug 13, 2026. Enrollment for this batch is closed.
          The next enrollment opens in November 2026. Exact date coming soon.
        </p>
        <div className="cs-price-badge">
          <div className="cs-pb-row">
            <span className="cs-pa">৳ 4,400</span>
            <span className="cs-pa-original">৳ 5,500</span>
            <span className="cs-pa-save">−20%</span>
          </div>
          <span className="cs-pl">/ Complete Course · Full Access</span>
        </div>

        <div className="cs-enroll-info-block">
          {(countLoading || enrollmentCount !== null) && (
            <div className="cs-enroll-count-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              {countLoading
                ? <span className="cs-count-spinner" aria-label="Loading count" />
                : <span className="cs-enroll-count-val">{enrollmentCount}</span>
              }
              &nbsp;students enrolled
            </div>
          )}
          <div className="cs-countdown-card">
            <div className="cs-cd-dates-row">
              <span className="cs-cd-date-pill cs-cd-offer">Class started Aug 13, 2026</span>
              <span className="cs-cd-date-pill cs-cd-class">Next enrollment · November 2026</span>
            </div>
            <div className="cs-cd-ended">
              Enrollment for this batch is closed.
              <span>Next enrollment: November 2026 · Exact date not selected yet</span>
            </div>
          </div>
        </div>

        <div className="cs-enroll-closed-card" role="status" aria-live="polite">
          <div className="cs-enroll-closed-badge">Enrollment Closed</div>
          <h3 className="cs-enroll-closed-title">Next Enrollment in November 2026</h3>
          <p className="cs-enroll-closed-desc">
            Exact start date has not been selected yet. Reach out and we&apos;ll notify you when seats open.
          </p>
          <div className="cs-enroll-closed-actions">
            <a href="mailto:arvixcreative@gmail.com?subject=Creative%20Samurai%20November%202026%20Waitlist" className="cs-btn-primary">
              Join Waitlist via Email
            </a>
            <a href="https://wa.me/8801645076455" target="_blank" rel="noopener noreferrer" className="cs-btn-ghost">
              WhatsApp Us
            </a>
          </div>
          <hr className="cs-form-divider" />
          <p className="cs-form-contact">
            Questions?{' '}
            <a href="mailto:arvixcreative@gmail.com" className="cs-form-email-link">
              arvixcreative@gmail.com
            </a>
          </p>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────── */}
      <footer className="cs-footer">
        <a href="/" className="cs-footer-logo" aria-label="Arvix Creative home">
          <img src={LOGO} alt="Arvix Creative" />
        </a>
        <div className="cs-footer-middle">
          <a href="mailto:arvixcreative@gmail.com" className="cs-footer-email">
            <span className="cs-footer-email-icon">✉</span> arvixcreative@gmail.com
          </a>
        </div>
        <div className="cs-footer-contacts">
          <a className="cs-footer-contact-link" href="mailto:arvixcreative@gmail.com">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
            arvixcreative@gmail.com
          </a>
          <a className="cs-footer-contact-link" href="https://wa.me/8801645076455" target="_blank" rel="noopener noreferrer">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
            </svg>
            01645-076455
          </a>
        </div>
        <div className="cs-footer-copy">© 2026 Arvix Creative · Arvix Academy</div>
      </footer>
    </div>
  );
};

export default CreativeSamuraiLanding;
